import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

export type ConfirmDialogVariant = "notice" | "confirm";

export interface ConfirmDialogProps {
  open: boolean;
  variant: ConfirmDialogVariant;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm?: () => void;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ConfirmDialog({
  open,
  variant,
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const isNotice = variant === "notice";
  const dismissible = isNotice;

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel) {
      const focusables = panel.querySelectorAll<HTMLElement>(
        FOCUSABLE_SELECTOR,
      );
      const target = focusables[focusables.length - 1] ?? panel;
      target.focus();
    }
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && dismissible) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = Array.from(
          panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !panel.contains(active)) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (active === last || !panel.contains(active)) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, dismissible, onClose]);

  if (!open) return null;

  const handleBackdrop = () => {
    if (dismissible) onClose();
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  const primaryLabel =
    confirmLabel ?? (isNotice ? "OK" : destructive ? "Delete" : "Confirm");

  const primaryClass = destructive
    ? "rounded bg-destructive text-destructive-foreground text-sm font-medium px-3.5 py-1.5 hover:bg-destructive/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-card"
    : "rounded bg-primary text-primary-foreground text-sm font-medium px-3.5 py-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card";

  const cancelClass =
    "rounded border border-border bg-background text-foreground text-sm font-medium px-3.5 py-1.5 hover:bg-muted transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card";

  const body =
    typeof message === "string" ? (
      <p
        id={descId}
        className="text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words"
      >
        {message}
      </p>
    ) : (
      <div id={descId} className="text-sm leading-relaxed text-foreground">
        {message}
      </div>
    );

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-dialog-backdrop"
      onClick={handleBackdrop}
      role="presentation"
    >
      <div
        ref={panelRef}
        role={isNotice ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-border bg-card shadow-xl animate-dialog-panel flex flex-col outline-none"
      >
        <div className="px-5 py-4 border-b border-border">
          <h2
            id={titleId}
            className="text-lg font-semibold text-foreground"
          >
            {title}
          </h2>
        </div>
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">{body}</div>
        <div className="px-5 py-3 border-t border-border flex justify-end gap-2 bg-muted/30 rounded-b-lg">
          {!isNotice && (
            <button
              type="button"
              className={cancelClass}
              onClick={onClose}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            className={primaryClass}
            onClick={isNotice ? onClose : handleConfirm}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
